import { Fragment, useCallback, useEffect, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { Icon } from '@iconify/react';
import Input, { InputProps } from './Input';
import InputGroup from './InputGroup';
import InputGroupAddon from './InputGroupAddon';
import Label from './Label';
import countryCodes from 'src/data/country-code.json';
import InputSearch from './InputSearch';

type InputPhoneProps = {
  htmlFor?: string;
  label?: string;
  handleSelectCode: (e: any) => void;
  labelClassName?: string;
  groupClassName?: string;
  addonClassName?: string;
} & InputProps;

const InputPhone = ({
  htmlFor,
  label,
  handleSelectCode,
  groupClassName,
  addonClassName,
  labelClassName,
  ...rest
}: InputPhoneProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCountryCodes, setFilteredCountryCodes] = useState(
    countryCodes?.filter((code) =>
      code.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const setData = useCallback(() => {
    setFilteredCountryCodes(
      countryCodes?.filter((code) =>
        code.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm]);

  useEffect(() => {
    setData();
  }, [setData]);

  const [selected, setSelected] = useState(filteredCountryCodes[0]);
  return (
    <>
      {label && (
        <Label className={labelClassName} htmlFor={htmlFor}>
          {label}
        </Label>
      )}
      <InputGroup className={groupClassName}>
        <InputGroupAddon className={addonClassName} addonType="prepend">
          <Listbox
            value={selected}
            onChange={(e) => {
              setSelected(e);
              handleSelectCode(e);
              setSearchTerm('');
            }}
          >
            {({ open }) => {
              if (open === false) {
                setSearchTerm('');
              }
              return (
                <div className="relative">
                  <Listbox.Button className="btn border rounded-l-full px-4 py-3.5 flex justify-center items-center gap-x-3">
                    <span className="text-sm text-placeholder">
                      {selected.code}
                    </span>
                    <span className="text-base text-[#292D32]">
                      <Icon icon="akar-icons:chevron-down" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="z-50 absolute mt-1 min-h-60 min-w-[16rem] max-w-[18rem] rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      <div className="sticky px-3 pt-2">
                        <InputSearch
                          onChange={(e: any) => setSearchTerm(e.target.value)}
                          placeholder="search code..."
                          className="py-1"
                        />
                      </div>
                      <div className=" max-h-60 bg-white overflow-auto">
                        {filteredCountryCodes.length === 0 &&
                        searchTerm !== '' ? (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700 text-center">
                            Nothing found.
                          </div>
                        ) : (
                          filteredCountryCodes.map((code, codeIdx) => (
                            <Listbox.Option
                              key={codeIdx}
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                  active
                                    ? 'bg-primary bg-opacity-10'
                                    : 'text-body-primary'
                                }`
                              }
                              value={code}
                            >
                              {({ selected }) => (
                                <>
                                  <span
                                    className={`block truncate ${
                                      selected
                                        ? 'font-medium text-primary'
                                        : 'font-normal text-body-primary'
                                    }`}
                                  >
                                    {code.name} {code.code}
                                  </span>
                                  {selected ? (
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                                      <Icon icon="akar-icons:check" />
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Listbox.Option>
                          ))
                        )}
                      </div>
                    </Listbox.Options>
                  </Transition>
                </div>
              );
            }}
          </Listbox>
        </InputGroupAddon>
        <Input {...rest} />
      </InputGroup>
    </>
  );
};

export default InputPhone;
